import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Divider,
  Modal,
  Skeleton,
  Space,
  Typography,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { JobApplication } from "../../../../Entities/JobApplication";
import applicationInsightService, {
  ApplicationInsightData,
} from "../../../../Service/application.insight.service";
import jobApplicationService from "../../../../Service/job.application.service";
import { openNotification } from "../../../../Components/Notification";
import { useLoader } from "../../../../Context/LoaderContext";
import SummaryBlock from "./SummaryBlock";
import BasisBlock from "./BasisBlock";
import SkillMatchBlock from "./SkillMatchBlock";
import RiskAndGapsBlock from "./RiskAndGapsBlock";
import AssessmentBlock from "./AssessmentBlock";
import FinalStepBlock from "./FinalStepBlock";
import "./ApplicationInsightCard.scss";

const POLL_INTERVAL_MS = 2000;

interface ApplicationInsightCardProps {
  jobApplicationId: string;
  jobApplication: JobApplication;
}

const ApplicationInsightCard: React.FC<ApplicationInsightCardProps> = ({
  jobApplicationId,
  jobApplication,
}) => {
  const [data, setData] = useState<ApplicationInsightData | null>(null);
  const [prevData, setPrevData] = useState<ApplicationInsightData | null>(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  const { setLoadingMessage, setLoading: setContextLoader } = useLoader();

  const shouldPoll = (d: ApplicationInsightData | null) =>
    d === null || d.status === "pending" || d.status === "partial";

  const aiJustArrived =
    prevData?.status === "partial" && data?.status === "completed";

  const fetchData = useCallback(async () => {
    try {
      const result =
        await applicationInsightService.getInsightByApplicationId(
          jobApplicationId,
        );
      setData((prev) => {
        setPrevData(prev);
        return result;
      });
      if (shouldPoll(result)) {
        pollRef.current = setTimeout(fetchData, POLL_INTERVAL_MS);
      }
    } catch {
      // non-fatal
    } finally {
      setLoading(false);
    }
  }, [jobApplicationId]);

  useEffect(() => {
    fetchData();
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [fetchData]);

  if (loading) {
    return (
      <div className="aic">
        <Skeleton active paragraph={{ rows: 4 }} />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const isPending = data.status === "pending";
  const isPartial = data.status === "partial";
  const isCompleted = data.status === "completed";
  const hasError = isCompleted && !!data.errorMessage;
  const isPostAssessment = data.phase === "post_assessment";

  const displayRounds = (data.assessmentRounds ?? []).filter(
    (r) => r.roundName.toLowerCase().replace(/\s+/g, "") !== "jobalignment",
  );
  const hasAssessments = displayRounds.length > 0;

  const mustHave = (data.skillEvidences ?? []).filter(
    (s) => s.category === "must_have",
  );
  const niceToHave = (data.skillEvidences ?? []).filter(
    (s) => s.category === "nice_to_have",
  );

  const isRiskAndGapAvailable =
    data.validationGaps?.length > 0 || data.riskIndicators?.length > 0;
  const isShowSkillBlock = mustHave.length > 0 || niceToHave.length > 0;
  const isShowRiskAndGaps =
    !isPostAssessment &&
    (data?.signalSummary?.topNextSteps ?? []).length === 0 &&
    isRiskAndGapAvailable;
  const isShowAssessments = isPostAssessment && hasAssessments;
  const isShowFinalSteps =
    isCompleted && (data?.signalSummary?.topNextSteps ?? []).length > 0;

  const handleDownloadreportClick = () => {
    if (!jobApplication) {
      return;
    }

    const executeDownload = async (maskPersonalDetails: boolean) => {
      try {
        setContextLoader(true);
        setLoadingMessage("Downloading Report...");
        const response = await jobApplicationService.downloadApplicationReport(
          jobApplication._id,
          maskPersonalDetails,
        );

        if (!response) {
          setContextLoader(false);
          setLoadingMessage(null);
          openNotification("error", "Failed to download", "please try again");
          return;
        }

        const disposition = response.headers?.["content-disposition"] ?? "";
        const utf8Match = disposition.match(/filename\*=UTF-8''([^;\n]+)/i);
        const serverFilename = utf8Match
          ? decodeURIComponent(utf8Match[1].trim())
          : disposition.match(/filename="?([^";\n]+)"?/i)?.[1]?.trim();
        const { firstName, lastName, phone } = jobApplication.user;
        const identifier =
          phone?.trim() || Math.random().toString(36).slice(2, 7);
        const fallbackFilename = `${firstName}_${lastName}-${identifier}.pdf`;

        const url = URL.createObjectURL(response.data as Blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = serverFilename ?? fallbackFilename;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 100);
        setContextLoader(false);
        setLoadingMessage(null);
        openNotification("success", "Report Downloaded", "");
      } catch (error: any) {
        console.error("handleDownloadreportClick ~ error:", error);
        setContextLoader(false);
        setLoadingMessage(null);
      }
    };

    const closeRef = { current: () => {} };
    const instance = Modal.confirm({
      title: "Download Report",
      centered: true,
      icon: null,
      content:
        "Do you want to mask the candidate's personal information (phone & email) in the downloaded report?",
      closable: true,
      maskClosable: false,
      footer: (
        <Space
          style={{ justifyContent: "flex-end", width: "100%", paddingTop: 8 }}
        >
          <Button
            onClick={() => {
              closeRef.current();
              executeDownload(false);
            }}
            danger
          >
            Download As-Is
          </Button>
          <Button
            type="primary"
            onClick={() => {
              closeRef.current();
              executeDownload(true);
            }}
            style={{ backgroundColor: "var(--ant-color-primary)" }}
          >
            Mask &amp; Download
          </Button>
        </Space>
      ),
    });
    closeRef.current = instance.destroy;
  };

  return (
    <div className="aic">
      {/* HEADER */}
      {data && (
        <div className="aic-header">
          <div className="aic-header-title">
            <span
              className="material-symbols-outlined"
              data-icon="auto_awesome"
              style={{ color: "#000000" }}
            >
              auto_awesome
            </span>
            <Typography.Title level={5} style={{ margin: 0 }}>
              AI EXECUTIVE SUMMARY
            </Typography.Title>
          </div>
          {isCompleted && !hasError && (
            <Button
              size="middle"
              icon={<DownloadOutlined />}
              onClick={handleDownloadreportClick}
              iconPosition="end"
            >
              Download Report
            </Button>
          )}
        </div>
      )}
      {data && <Divider style={{ margin: 0 }} />}

      {/* SUMMARY */}
      {data && (
        <SummaryBlock
          isPending={isPending}
          isPartial={isPartial}
          isPostAssessment={isPostAssessment}
          overallReasoning={data.overallReasoning}
          executiveSummary={data.executiveSummary}
          fitCategory={data.fitCategory}
          aiJustArrived={aiJustArrived}
        />
      )}
      {data && <BasisBlock basisOfInsight={data.basisOfInsight} />}

      {/* SKILL MATCH */}
      {isShowSkillBlock && (
        <div className="aic-header">
          <div className="aic-header-title">
            <span
              className="material-symbols-outlined"
              data-icon="find_in_page"
              style={{ color: "#000000" }}
            >
              find_in_page
            </span>
            <Typography.Title level={5} style={{ margin: 0 }}>
              SKILL MATCH
            </Typography.Title>
          </div>
        </div>
      )}
      {isShowSkillBlock && <Divider style={{ margin: 0 }} />}
      {isShowSkillBlock && (
        <SkillMatchBlock mustHave={mustHave} niceToHave={niceToHave} />
      )}

      {/* RISK & GAPS */}
      {isShowRiskAndGaps && (
        <div className="aic-header">
          <div className="aic-header-title">
            <span
              className="material-symbols-outlined"
              data-icon="query_stats"
              style={{ color: "#000000" }}
            >
              query_stats
            </span>
            <Typography.Title level={5} style={{ margin: 0 }}>
              RISK & GAPS
            </Typography.Title>
          </div>
        </div>
      )}
      {isShowRiskAndGaps && <Divider style={{ margin: 0 }} />}
      {isShowRiskAndGaps && (
        <RiskAndGapsBlock
          validationGaps={data.validationGaps ?? []}
          riskIndicators={data.riskIndicators ?? []}
        />
      )}

      {/* ASSESSMENT ROUNDS */}
      {isShowAssessments && (
        <div className="aic-header">
          <div className="aic-header-title">
            <span
              className="material-symbols-outlined"
              data-icon="quiz"
              style={{ color: "#000000" }}
            >
              quiz
            </span>
            <Typography.Title level={5} style={{ margin: 0 }}>
              ASSESSMENTS
            </Typography.Title>
          </div>
          <Typography.Text type="secondary">
            {displayRounds.filter((r) => r.status === "qualified").length} of{" "}
            {displayRounds.length} rounds qualified
            {displayRounds.some((r) => r.avgEvaluationScore !== null) &&
              ` · ${Math.round(
                displayRounds
                  .filter((r) => r.avgEvaluationScore !== null)
                  .reduce((s, r) => s + r.avgEvaluationScore!, 0) /
                  displayRounds.filter((r) => r.avgEvaluationScore !== null)
                    .length,
              )}/100 avg`}
          </Typography.Text>
        </div>
      )}
      {isShowAssessments && <Divider style={{ margin: 0 }} />}
      {isShowAssessments && (
        <AssessmentBlock
          displayRounds={displayRounds}
          signalSummary={data.signalSummary}
        />
      )}

      {/* FINAL STEPS */}
      {isShowFinalSteps && <Divider style={{ margin: 0 }} />}
      {isShowFinalSteps && (
        <FinalStepBlock topNextSteps={data.signalSummary?.topNextSteps ?? []} />
      )}

      {hasError && (
        <Alert
          type="warning"
          message="AI analysis unavailable"
          description="Scores and statuses are based on resume data."
          showIcon
          style={{ margin: "8px 0" }}
        />
      )}
    </div>
  );
};

export default ApplicationInsightCard;
